/**
 * versionSelfHeal.js — v2.5.914+ FINALITY FIX for cross-environment version drift.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Problem this solves permanently:
 *   Preview showed v2.5.739. Production showed v2.5.906. Source was v2.5.914.
 *   All three environments were reading different `PLATFORM_VERSION` constants
 *   baked into different JS bundles — service-worker caches held ancient
 *   bundles that a user's browser would happily continue to run for months
 *   even after the backend was updated 200 times.
 *
 * The self-heal (runs once on every page load, before React mounts):
 *   1. Fetch the backend's `/api/version` (this is the truth — the backend
 *      reads `version.js` from disk at request time, so it CANNOT be stale).
 *   2. Compare the backend version against the frontend's compiled-in
 *      PLATFORM_VERSION constant.
 *   3. If they differ:
 *        a. Set a session-scoped `konnect_heal_attempted` flag so we never
 *           heal twice in the same tab (prevents infinite reload loops if the
 *           new bundle also happens to be stale for some weird reason).
 *        b. `postMessage('KONNECT_SW_PURGE')` to the active service worker →
 *           this unregisters the SW, deletes every cache, and hard-navigates
 *           every controlled client.
 *        c. Fallback: manually `caches.delete()` every cache + unregister
 *           every SW registration + `window.location.reload(true)`.
 *   4. Also stamps `window.__konnect_backend_version` so React components can
 *      display the live backend version instead of a compile-time constant.
 *
 * Result: A user landing on the site with a 200-versions-stale bundle sees
 *   ONE flash of the old bundle → auto-heals → ONE hard-reload → correct
 *   bundle forever. Zero manual "clear cache" instructions. Zero support
 *   tickets. Zero version drift across environments.
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { PLATFORM_VERSION } from './config/version';

const HEAL_SESSION_FLAG = 'konnect_heal_attempted_v1';
const BACKEND_VERSION_GLOBAL = '__konnect_backend_version';

// Rough semver compare. Returns 1 if a > b, -1 if a < b, 0 if equal.
function cmpVersions(a, b) {
  const pa = String(a || '0').split('.').map(n => parseInt(n, 10) || 0);
  const pb = String(b || '0').split('.').map(n => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i] || 0, y = pb[i] || 0;
    if (x > y) return 1;
    if (x < y) return -1;
  }
  return 0;
}

async function nukeCachesAndReload() {
  try {
    // 1. Kill service worker registrations
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister().catch(() => {})));
    }
    // 2. Delete every cache the browser has for this origin
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map(n => caches.delete(n).catch(() => {})));
    }
  } catch { /* proceed to reload regardless */ }
  // 3. Add a cache-buster query and hard-reload
  const bust = `_ktv=${Date.now()}`;
  const sep = window.location.href.includes('?') ? '&' : '?';
  // Only append the cache-buster if it's not already on the URL — avoids
  // pollution when multiple self-heal cycles happen legitimately.
  const target = window.location.href.includes('_ktv=')
    ? window.location.href
    : window.location.href + sep + bust;
  window.location.replace(target);
}

export async function runVersionSelfHeal() {
  // Never fire more than once per tab session.
  try {
    if (sessionStorage.getItem(HEAL_SESSION_FLAG)) return;
  } catch { /* storage blocked — safe to try once */ }

  // v2.6.10 finality — ONE-SHOT global reset for the vending-tour cache-lock
  // bug. Users on production have SW v8 caching the old 10-step main.*.js.
  // Simply bumping SW to v9 doesn't dislodge already-active v8 controllers
  // until every tab closes. This flag fires exactly once per browser to
  // fully unregister the SW, purge every cache, and hard-reload. After the
  // first visit under 2.6.10+, this branch is inert forever.
  const ONESHOT_KEY = 'konnect_oneshot_heal_v2_6_10';
  try {
    if (!localStorage.getItem(ONESHOT_KEY)) {
      localStorage.setItem(ONESHOT_KEY, String(Date.now()));
      try { sessionStorage.setItem(HEAL_SESSION_FLAG, 'oneshot-2.6.10'); } catch { /* storage blocked */ }
      await nukeCachesAndReload();
      return;
    }
  } catch { /* storage blocked — fall through to normal drift check */ }

  const apiBase = process.env.REACT_APP_BACKEND_URL || '';
  if (!apiBase) return;

  let backendVersion = '';
  try {
    const r = await fetch(`${apiBase}/api/version`, { cache: 'no-store' });
    if (!r.ok) return;
    const d = await r.json();
    backendVersion = (d && d.version) || '';
  } catch { return; /* backend unreachable — nothing we can do here */ }

  if (!backendVersion) return;

  // Stamp for React components (Sidebar uses this via a normal fetch, but we
  // set the global anyway so any future component can synchronously read it
  // without another network call).
  try { window[BACKEND_VERSION_GLOBAL] = backendVersion; } catch { /* frozen window */ }

  // Only self-heal when the backend is STRICTLY NEWER than the bundle.
  // If bundle > backend (which can happen briefly right after a deploy where
  // the frontend rebuilt first), we do nothing — the backend restart will
  // catch up shortly and no cache-purge is needed on the client.
  if (cmpVersions(backendVersion, PLATFORM_VERSION) <= 0) return;

  // Mark BEFORE reloading so the next boot sees the flag and skips.
  try { sessionStorage.setItem(HEAL_SESSION_FLAG, `${PLATFORM_VERSION}->${backendVersion}`); } catch { /* storage blocked */ }

  // Try the SW purge broadcast first (fastest path when the SW is alive).
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    try {
      navigator.serviceWorker.controller.postMessage('KONNECT_SW_PURGE');
      // The SW listener navigates all clients itself — but if it hangs, our
      // 800ms fallback will nuke + reload manually.
      setTimeout(nukeCachesAndReload, 800);
      return;
    } catch { /* fall through */ }
  }
  await nukeCachesAndReload();
}

// Also expose a manual trigger for admin buttons.
export function forceSelfHeal() {
  try { sessionStorage.removeItem(HEAL_SESSION_FLAG); } catch { /* storage blocked */ }
  return nukeCachesAndReload();
}
